import {action, makeAutoObservable, observable} from "mobx";
import CommonStore from "../../../stores/CommonStore";
import Currency from "../../../entities/Currency";
import MoneyExchangeRateRange from "../../../entities/productOrder/moneyDelivery/MoneyExchangeRateRange";
import MoneyExchangeRate from "../../../entities/productOrder/moneyDelivery/MoneyExchangeRate";
import { MoneyExchangeService } from "../../../service/moneyDelivery/MoneyExchangeRateService";
// import {MoneyExchangeRateService} from "../../../service/MoneyExchangeRateService"; // Assumindo que existe um serviço similar

const ERROR_MAP = {
    currency: ["ERROR_00_400_36"],
    ranges: ["ERROR_00_400_37"],
    rangeValues: ["ERROR_00_500_58"],
}

class NewMoneyExchangeRateStore {

    public moneyExchangeRateId?: string;
    public agencyIds?: string[];
    public moneyExchangeRate: MoneyExchangeRate;
    public isEditing: boolean;
    public isModalOpen: boolean;
    public loading: boolean;
    public commonStore: CommonStore;

    public currencyError?: string;
    public rangesError?: string;

    constructor(commonStore: CommonStore, moneyExchangeRateId?: string, agencyIds?: string[]) {
        this.isEditing = !!moneyExchangeRateId;
        this.moneyExchangeRateId = moneyExchangeRateId;
        this.agencyIds = agencyIds;
        this.isModalOpen = false;
        this.moneyExchangeRate = {
            id: '',
            agencyId: agencyIds && agencyIds.length > 0 ? agencyIds[0] : null,
            exchangeRateRanges: [
                new MoneyExchangeRateRange(0, 1000000, 0)
            ]
        } as MoneyExchangeRate;
        this.loading = false;
        this.commonStore = commonStore;
        makeAutoObservable(this, {
            moneyExchangeRate: observable,
        });
    }

    get enableButton(): boolean {
        const ranges = this.moneyExchangeRate?.exchangeRateRanges ?? [];

        if (!this.moneyExchangeRate?.currency || ranges.length === 0) {
            return false;
        }
        
        for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];

            for (let j = 0; j < ranges.length; j++) {
                if (i === j) continue;

                const other = ranges[j];

                const overlaps =
                    (range.fromValue >= other.fromValue && range.fromValue <= other.toValue) ||
                    (range.toValue   >= other.fromValue && range.toValue   <= other.toValue);

                if (overlaps) {
                    return false;
                }
            }
        }

        return true;
    }

    @action
    public async loadMoneyExchangeRate() {
        if (this.moneyExchangeRateId) {
            this.isEditing = true;
            const response = await MoneyExchangeService.find(this.moneyExchangeRateId!);
            if (response.success && response.data) {
                this.moneyExchangeRate = response.data;
            }
            this.commonStore.processErrors(response);
        }
    }

    @action
    public async openModal() {
        this.loading = true;
        await this.loadMoneyExchangeRate();
        this.isModalOpen = true;
        this.loading = false;
    }

    @action
    public closeModal() {
        this.isModalOpen = false;
    }

    @action
    public addExchangeRateRange() {
        const lastRange = this.moneyExchangeRate.exchangeRateRanges[this.moneyExchangeRate.exchangeRateRanges.length - 1];
        const newFromValue = lastRange ? lastRange.toValue + 1 : 0;
        const newRange = new MoneyExchangeRateRange(newFromValue, newFromValue + 100, 0);
        this.moneyExchangeRate.exchangeRateRanges.push(newRange);
        this.moneyExchangeRate = { ...this.moneyExchangeRate };
    }

    @action
    public removeExchangeRateRange(index: number) {
        if (this.moneyExchangeRate.exchangeRateRanges.length > 1) {
            this.moneyExchangeRate.exchangeRateRanges.splice(index, 1);
        }
        this.moneyExchangeRate = { ...this.moneyExchangeRate };
    }

    @action
    public updateExchangeRateRange(index: number, field: 'fromValue' | 'toValue' | 'value', value: number) {
        if (this.moneyExchangeRate.exchangeRateRanges[index]) {
            this.moneyExchangeRate.exchangeRateRanges[index][field] = value;
        }
        this.moneyExchangeRate = { ...this.moneyExchangeRate };
    }

    @action
    public updateCurrency(currency: Currency) {
        this.moneyExchangeRate.currency = currency;
    }

    @action
    public async save(): Promise<boolean> {
        this.loading = true;
        let response: any;
        if (!this.agencyIds || this.agencyIds.length === 1) {
            const serviceToCall = this.isEditing ? MoneyExchangeService.update : MoneyExchangeService.create;
            response = await serviceToCall(this.moneyExchangeRate);
        } else if (this.agencyIds && this.agencyIds.length > 1) {
            response = await MoneyExchangeService.updateSeveral(this.agencyIds, this.moneyExchangeRate);
        }
        this.commonStore.processErrors(response);
        this.processError(response.error);
        this.loading = false;
        return response.success;
    }

    processError(error?: string) {
        this.currencyError = undefined;
        this.rangesError = undefined;
        if (error) {
            if (ERROR_MAP.currency.includes(error)) {
                this.currencyError = error;
            }
            if (ERROR_MAP.ranges.includes(error)) {
                this.rangesError = error;
            }
        }
    }
}

export default NewMoneyExchangeRateStore;

